const
  PiBy180 = Math.PI / 180,
  PiBy2 = Math.PI / 2;

export const MathUtils = {
  radiansToDegrees(radians) {
    return radians / PiBy180;
  },

  degreesToRadians: function(degrees) {
    return degrees * PiBy180;
  },

  sin(angle) {
    if (angle === 0) { return 0; }
    let angleSlice = angle / PiBy2, sign = 1;
    if (angle < 0) {
      // sin(-a) = -sin(a)
      sign = -1;
    }
    switch (angleSlice) {
      case 1: return sign;
      case 2: return 0;
      case 3: return -sign;
    }
    return Math.sin(angle);
  },

  cos(angle) {
    if (angle === 0) { return 1; }
    if (angle < 0) {
      // cos(a) = cos(-a)
      angle = -angle;
    }
    let angleSlice = angle / PiBy2;
    switch (angleSlice) {
      case 1: case 3: return 0;
      case 2: return -1;
    }
    return Math.cos(angle);
  },

  rotatePoint(vec: Vector, origin, radians) {
    vec.sub(origin);
    const v = this.rotateVector(vec, radians);
    return new Vector({ x: v.x, y: v.y }).add(origin);
  },

  rotateVector(vec: Vector, radians) {
    const sin = MathUtils.sin(radians),
      cos = MathUtils.cos(radians),
      rx = vec.x * cos - vec.y * sin,
      ry = vec.x * sin + vec.y * cos;
    return new Vector({ x: rx, y: ry });
  },

  transform(vec: Vector, t, ignoreOffset) {
    if (ignoreOffset) {
      return new Vector({
        x: t[0] * vec.x + t[2] * vec.y,
        y: t[1] * vec.x + t[3] * vec.y
      });
    }
    return new Vector({
      x: t[0] * vec.x + t[2] * vec.y + t[4],
      y: t[1] * vec.x + t[3] * vec.y + t[5]
    });
  }
}

export class Vector {
  public x;
  public y;

  constructor(vec = { x: 0, y: 0 }) {
    this.x = vec.x;
    this.y = vec.y;
  }

  from(x, y) {
    this.x = x ?? 0;
    this.y = y ?? 0;
    return this;
  }

  rotate(radians, origin) {
    const vec = MathUtils.rotatePoint(this, origin, radians);
    this.copy(vec);
    return this;
  }

  sub(vec: Vector) {
    this.x -= vec.x;
    this.y -= vec.y;
    return this;
  }

  add(vec: Vector) {
    this.x += vec.x;
    this.y += vec.y;
    return this;
  }

  copy(vec: Vector) {
    this.x = vec.x;
    this.y = vec.y;
    return this;
  }

  angle(vec: Vector) {
    return Math.atan2(this.y - vec.y, this.x - vec.x);
  }

  distance(vec: Vector) {
    const dx = this.x - vec.x;
    const dy = this.y - vec.y;
    return Math.sqrt(dx * dx + dy * dy);
  }
}